import {AnimatePresence} from 'framer-motion';
import {FC, useEffect, useRef, useState} from 'react';
import * as Styles from './tooltip.styles';
import {usePlayerContext} from '../../hooks/usePlayerContext';
import {md} from '../../variables.styles';
import {getNumberFromEmAndBaseFont} from '../../utils/getNumberFromEmAndBaseFont';

export type Position = string | 'center';

/**
 * The type of tooltip to render.
 * 'info' tooltips display information to the user.
 * 'context' tooltips provide context to some UI element
 */
export type TooltipType = 'info' | 'context';

interface TooltipProps {
  label: string;
  keycode?: string;
  type?: TooltipType;
  position?: Position;
  visible: boolean;
}

const hidden = {opacity: 0, y: '25%'};

export const Tooltip: FC<TooltipProps> = ({
  label,
  type = 'info',
  position,
  visible,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const {player} = usePlayerContext();
  const [offset, setOffset] = useState(0);

  // Shift the position so the tooltip does not overflow the safe area
  // of the player container.
  // Note: currently only supports checking in the x dimension.
  useEffect(() => {
    if (!player || !ref.current) return;

    const $player = player.getContainer();

    // Calculate the visual safe zone of the player.
    const fontSize = getComputedStyle($player, null).getPropertyValue(
      'font-size'
    );

    try {
      const safeZonePadding = getNumberFromEmAndBaseFont(fontSize, md);

      const {left: playerLeft, right: playerRight} =
        $player.getBoundingClientRect();
      const {
        right: tooltipRight,
        left: tooltipLeft,
        width: tooltipWidth,
      } = ref.current.getBoundingClientRect();

      // Because we translate the tooltip `-50%` in the x dimension in order to
      // center it over the target element, we need to shift our edges over by
      // half the tooltip width to compensate.
      const actualToolTipLeft = tooltipLeft - tooltipWidth / 2;
      const actualTTRight = tooltipRight - tooltipWidth / 2;

      let overflowAmmount = 0;

      if (actualToolTipLeft < playerLeft + safeZonePadding) {
        overflowAmmount = actualToolTipLeft - playerLeft - safeZonePadding;
      } else if (actualTTRight > playerRight - safeZonePadding) {
        overflowAmmount = actualTTRight - playerRight + safeZonePadding;
      }

      setOffset(Math.floor(overflowAmmount));
    } catch (err) {
      console.error('An error occured calculating Tooltip offset: ', err);
    }
  }, [player, ref, visible, label]);

  return (
    <AnimatePresence>
      {visible && (
        <Styles.Wrapper
          ref={ref}
          key="tooltip"
          role="tooltip"
          initial={hidden}
          animate={{opacity: 1, y: 0}}
          exit={hidden}
          position={position}
          aria-hidden={type === 'info' ? false : true}
          data-testid="tooltip"
        >
          <Styles.Container type={type} offset={offset}>
            <Styles.Label>{label}</Styles.Label>
          </Styles.Container>
        </Styles.Wrapper>
      )}
    </AnimatePresence>
  );
};
